import { supabase } from "@/integrations/supabase/client";

// Alta en la newsletter (footer de la home y footer del resto de la web). Antes se mostraba «listo»
// y se mandaban la bienvenida y el aviso a admin aunque la BD rechazase el alta.
// Misma validación que la política de newsletter_subscribers (si no, el insert se rechaza).
const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
const DB_LANGS = ["en", "es", "it", "fr", "pt", "ar", "nl", "zh"];

export type NewsletterResult = "subscribed" | "already" | "invalid" | "error";

export const isNewsletterEmail = (email: string) => EMAIL_RE.test(email.trim());

export async function subscribeNewsletter(rawEmail: string, lang: string): Promise<NewsletterResult> {
  const email = rawEmail.trim().toLowerCase();
  if (!EMAIL_RE.test(email) || email.length > 255) return "invalid";
  const language = DB_LANGS.includes(lang) ? lang : "en";
  const { error } = await supabase.from("newsletter_subscribers").insert({ email, language } as never);
  if (error) {
    if (error.code === "23505" || /duplicate|unique/i.test(error.message || "")) return "already";
    console.error("Newsletter subscription error:", error);
    return "error";
  }
  // Bienvenida al suscriptor + aviso a admin@ulture.co (la plantilla de aviso lee `email`).
  const results = await Promise.allSettled([
    supabase.functions.invoke("send-transactional-email", {
      body: { templateName: "newsletter-welcome", recipientEmail: email, idempotencyKey: `newsletter-welcome-${email}`, templateData: { email, language: lang } },
    }),
    supabase.functions.invoke("send-transactional-email", {
      body: { templateName: "newsletter-admin-notification", recipientEmail: "admin@ulture.co", idempotencyKey: `newsletter-admin-${email}`, templateData: { email, language: lang } },
    }),
  ]);
  results.forEach((r) => {
    if (r.status === "rejected" || (r.value as { error?: unknown } | undefined)?.error) console.error("Newsletter email failed:", r);
  });
  return "subscribed";
}

// Avisos del formulario en el idioma de la web.
export const NEWSLETTER_MSG: Record<string, { invalid: string; error: string }> = {
  es: { invalid: "Escribe un email válido.", error: "No se ha podido enviar. Inténtalo de nuevo." },
  en: { invalid: "Enter a valid email address.", error: "We couldn't send it. Please try again." },
  it: { invalid: "Inserisci un indirizzo email valido.", error: "Invio non riuscito. Riprova." },
  fr: { invalid: "Saisissez une adresse e-mail valide.", error: "L’envoi a échoué. Réessayez." },
  nl: { invalid: "Vul een geldig e-mailadres in.", error: "Versturen is niet gelukt. Probeer het opnieuw." },
  pt: { invalid: "Escreva um email válido.", error: "Não foi possível enviar. Tente novamente." },
  ar: { invalid: "يُرجى إدخال بريد إلكتروني صحيح.", error: "تعذّر الإرسال. حاول مرة أخرى." },
  zh: { invalid: "请输入有效的邮箱地址。", error: "发送失败，请重试。" },
};
